import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription }      from 'rxjs';
import { SignalRService }    from 'src/app/core/services/signalr.service';
import { AiInsightsService } from 'src/app/features/services/ai-insights.service';
import { AiInsight } from '../../models/ai-insight.model';

@Component({
  selector:    'app-ai-insights-panel',
  templateUrl: './ai-insights-panel.component.html',
  styleUrls:   ['./ai-insights-panel.component.css']
})
export class AiInsightsPanelComponent implements OnInit, OnDestroy {

  insights:    AiInsight[] = [];
  unreadCount  = 0;
  loading      = true;
  runningCheck = false;
  markingAll   = false;

  private subs: Subscription[] = [];

  constructor(
    private aiService: AiInsightsService,
    private signalR:   SignalRService
  ) {}

  ngOnInit(): void {
    // Subscribe to shared BehaviorSubject streams so any update
    // from any source (API load, SignalR push, mark-read) reflects
    // in this component immediately.
    this.subs.push(
      this.aiService.insights$.subscribe(items => {
        this.insights = items;
      })
    );

    this.subs.push(
      this.aiService.unreadCount$.subscribe(n => {
        this.unreadCount = n;
      })
    );

    // Load existing insights from the API immediately on open.
    this.loadInsights();

    // Subscribe to real-time SignalR push. When the API background
    // service generates a new insight, it is pushed here instantly
    // and the panel updates without a page refresh.
    this.signalR.on('AiInsightCreated', (raw: any) => {
      console.log('AI insight received via SignalR:', raw);
      this.aiService.pushRealTime(raw);
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  loadInsights(): void {
    this.loading = true;
    this.aiService.load().subscribe({
      next: (res) => {
        console.log(`AI Insights loaded: ${res.total} total, ${res.unreadCount} unread.`);
        this.loading = false;
      },
      error: (err) => {
        console.error('AI Insights load failed:', err);
        this.loading = false;
      }
    });
  }

  markAsRead(insight: AiInsight, event: Event): void {
    event.stopPropagation();
    if (insight.isRead) return;
    this.aiService.markAsRead(insight.id).subscribe();
  }

  markAllRead(): void {
    if (this.markingAll || this.unreadCount === 0) return;
    this.markingAll = true;
    this.aiService.markAllRead().subscribe({
      complete: () => { this.markingAll = false; }
    });
  }

  dismiss(insight: AiInsight, event: Event): void {
    event.stopPropagation();
    this.aiService.delete(insight.id).subscribe();
  }

  runNow(): void {
    if (this.runningCheck) return;
    this.runningCheck = true;

    this.aiService.runNow().subscribe({
      next: () => {
        // After triggering the check, wait 3 seconds then reload
        // to pick up any newly generated insights.
        setTimeout(() => {
          this.loadInsights();
          this.runningCheck = false;
        }, 3000);
      },
      error: () => {
        this.runningCheck = false;
      }
    });
  }

  typeIcon(type: string): string {
    return ({
      Delay:       '⏱',
      Inactivity:  '🧍',
      Performance: '📊',
      Pattern:     '📉'
    } as any)[type] ?? '🤖';
  }

  typeLabel(type: string): string {
    return ({
      Delay:       'Job Delay',
      Inactivity:  'Worker Inactive',
      Performance: 'Performance',
      Pattern:     'Pattern Detected'
    } as any)[type] ?? type;
  }

  severityIcon(s: string): string {
    return ({ High: '🚨', Medium: '⚠️', Low: '💡' } as any)[s] ?? '💡';
  }

  severityClass(s: string): string {
    return ({
      High:   'aii__card--high',
      Medium: 'aii__card--medium',
      Low:    'aii__card--low'
    } as any)[s] ?? 'aii__card--low';
  }

  trackById(_: number, item: AiInsight): string {
    return item.id;
  }
}